import { useState, useEffect } from 'react';
import {
  getCompanies,
  getCompanyStats,
  createCompany,
  updateCompany,
  deleteCompany,
  type Company,
  type CompanyStats,
  type CreateCompanyData,
} from '../../api/companies.api';
import { getPlans, type Plan } from '../../api/plans.api';
import { getDevices, blockDevice, unblockDevice, revokeDevice, reactivateDevice, assignDeviceSede, type Device } from '../../api/devices.api.ts';
import { getSedes, type Sede } from '../../api/sedes.api';
import { getActivationCodes, updateActivationCode, type ActivationCode } from '../../api/activation.api.ts';

import { createUser, type CreateUserData } from '../../api/users.api';
import { formatCOP } from '../../utils/currencyFormat';

export default function Empresas() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showActivationsModal, setShowActivationsModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [companyDevices, setCompanyDevices] = useState<Device[]>([]);
  const [companySedes, setCompanySedes] = useState<Sede[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [activeActivationCode, setActiveActivationCode] = useState<ActivationCode | null>(null);
  const [isUpdatingLimit, setIsUpdatingLimit] = useState(false);
  const [newMaxDevices, setNewMaxDevices] = useState<number>(1);
  const [offlineDays, setOfflineDays] = useState<number>(7);
  const [isSavingOfflineDays, setIsSavingOfflineDays] = useState(false);
  const [offlineDaysError, setOfflineDaysError] = useState<string | null>(null);


  // Estado de datos
  const [companies, setCompanies] = useState<Company[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [stats, setStats] = useState<CompanyStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Formulario
  const [formData, setFormData] = useState<CreateCompanyData>({
    nombre: '',
    nit: '',
    direccion: '',
    telefono: '',
    email: '',
    billingEnabled: false,
    razonSocial: '',
    cityId: '',
    taxRegimeId: '',
    factusClientId: '',
    factusClientSecret: '',
    factusUsername: '',
  });

  const [userFormData, setUserFormData] = useState<CreateUserData>({
    nombre: '',
    email: '',
    password: '',
    rol: 'ADMIN',
    telefono: '',
    companyId: '',
  });

  // Cargar datos al montar


  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [companiesData, statsData, plansData] = await Promise.all([
        getCompanies(),
        getCompanyStats(),
        getPlans(),
      ]);
      setCompanies(companiesData);
      setStats(statsData);
      setPlans(plansData);
    } catch (err) {
      console.error('Error cargando datos:', err);
      setError('Error al cargar las empresas');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.nit.includes(searchTerm);
    const matchesPlan = filterPlan === 'all' || company.plan?.nombre === filterPlan;
    const matchesStatus = filterStatus === 'all' || company.status === filterStatus;
    return matchesSearch && matchesPlan && matchesStatus;
  });

  const getPlanColor = (plan: string | undefined) => {
    switch (plan) {
      case 'Enterprise': return 'bg-purple-100 text-purple-700';
      case 'Profesional': return 'bg-teal-100 text-teal-700';
      case 'Básico': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVA': return 'bg-green-100 text-green-700';
      case 'INACTIVA': return 'bg-red-100 text-red-700';
      case 'PRUEBA': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVA': return 'Activa';
      case 'INACTIVA': return 'Inactiva';
      case 'PRUEBA': return 'En Prueba';
      default: return status;
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      nit: '',
      direccion: '',
      telefono: '',
      email: '',
      billingEnabled: false,
      razonSocial: '',
      cityId: '',
      taxRegimeId: '',
      factusClientId: '',
      factusClientSecret: '',
      factusUsername: '',
      factusPassword: '',
      planId: '',
    });
  };

  // Funciones de validación de campos
  const handleNombreChange = (value: string) => {
    // Solo permitir letras, espacios y acentos
    const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/;
    if (regex.test(value)) {
      setFormData({ ...formData, nombre: value });
    }
  };

  const handleNitChange = (value: string) => {
    // Solo permitir números y máximo 13 dígitos
    const regex = /^[0-9]{0,13}$/;
    if (regex.test(value)) {
      setFormData({ ...formData, nit: value });
    }
  };

  const handleTelefonoChange = (value: string) => {
    // Solo permitir números y máximo 10 dígitos (estándar común, ajustable si es necesario)
    const regex = /^[0-9]{0,15}$/;
    if (regex.test(value)) {
      setFormData({ ...formData, telefono: value });
    }
  };

  const handleOpenAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleOpenEdit = (company: Company) => {
    setSelectedCompany(company);
    setFormData({
      nombre: company.nombre,
      nit: company.nit,
      direccion: company.direccion || '',
      telefono: company.telefono || '',
      email: company.email || '',
      // Billing fields handled separately now, or kept empty here. 
      // Keeping basic structure consistent for types, but ignoring billing values for Edit Modal initial load.
      billingEnabled: false,
      razonSocial: '',
      cityId: '',
      taxRegimeId: '',
      factusClientId: '',
      factusClientSecret: '',
      factusUsername: '',
      factusPassword: '',
      planId: company.plan?.id || '',
    });
    setShowEditModal(true);
  };

  const handleOpenDelete = (company: Company) => {
    setSelectedCompany(company);
    setDeleteConfirmText('');
    setError(null);
    setShowDeleteModal(true);
  };

  const handleViewDetails = (company: Company) => {
    setSelectedCompany(company);
    setShowDetailsModal(true);
  };

  const handleViewActivations = async (company: Company) => {
    setSelectedCompany(company);
    setOfflineDays(company.offlineGraceDays ?? 7);
    setOfflineDaysError(null);
    setShowActivationsModal(true);
    setIsLoadingDevices(true);
    setActiveActivationCode(null);
    try {
      const [devices, codes, sedes] = await Promise.all([
        getDevices(company.id),
        getActivationCodes(company.id),
        getSedes(company.id)
      ]);
      setCompanyDevices(devices);
      setCompanySedes(sedes.filter((sede) => sede.activa));

      // Encontrar el código activo
      const activeCode = codes.find(c => c.status === 'ACTIVE' || c.status === 'EXHAUSTED');
      if (activeCode) {
        setActiveActivationCode(activeCode);
        setNewMaxDevices(activeCode.maxDevices);
      }
    } catch (err) {
      console.error('Error cargando dispositivos:', err);
      setError('Error al cargar los dispositivos activados');
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const handleUpdateMaxDevices = async () => {
    if (!activeActivationCode) return;
    setIsUpdatingLimit(true);
    try {
      await updateActivationCode(activeActivationCode.id, { maxDevices: newMaxDevices });
      setSuccessMessage('Límite de dispositivos actualizado');
      setTimeout(() => setSuccessMessage(null), 3000);

      // Recargar datos
      if (selectedCompany) {
        handleViewActivations(selectedCompany);
      }
    } catch (err) {
      console.error('Error actualizando límite:', err);
      setError('Error al actualizar el límite de dispositivos');
    } finally {
      setIsUpdatingLimit(false);
    }
  };

  const handleSaveOfflineDays = async () => {
    if (!selectedCompany) return;
    if (!Number.isInteger(offlineDays) || offlineDays < 1 || offlineDays > 365) {
      setOfflineDaysError('Ingresa un número entero entre 1 y 365');
      return;
    }
    setOfflineDaysError(null);
    setIsSavingOfflineDays(true);
    try {
      await updateCompany(selectedCompany.id, { offlineGraceDays: offlineDays });
      setSelectedCompany({ ...selectedCompany, offlineGraceDays: offlineDays });
      setCompanies(prev => prev.map(c => (c.id === selectedCompany.id ? { ...c, offlineGraceDays: offlineDays } : c)));
      setSuccessMessage('Días sin conexión actualizados. Cada equipo aplica el nuevo plazo en su próxima sincronización.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      console.error('Error actualizando días sin conexión:', err);
      const message = err.response?.data?.message;
      setOfflineDaysError(Array.isArray(message) ? message.join(', ') : message || 'Error al actualizar los días sin conexión');
    } finally {
      setIsSavingOfflineDays(false);
    }
  };

  // Fecha en que el equipo se bloquea si no vuelve a sincronizar
  const getOfflineDeadline = (device: Device) => {
    if (!device.lastSyncAt) return null;
    const days = selectedCompany?.offlineGraceDays ?? 7;
    return new Date(new Date(device.lastSyncAt).getTime() + days * 24 * 60 * 60 * 1000);
  };

  const handleBlockDevice = async (device: Device) => {
    if (!selectedCompany) return;
    try {
      await blockDevice(device.id, 'Bloqueado por Super Admin');
      setSuccessMessage('Dispositivo bloqueado correctamente');
      setTimeout(() => setSuccessMessage(null), 3000);
      handleViewActivations(selectedCompany);
    } catch (err) {
      console.error('Error bloqueando dispositivo:', err);
      setError('Error al bloquear el dispositivo');
    }
  };

  const handleUnblockDevice = async (device: Device) => {
    if (!selectedCompany) return;
    try {
      await unblockDevice(device.id);
      setSuccessMessage('Dispositivo desbloqueado correctamente');
      setTimeout(() => setSuccessMessage(null), 3000);
      handleViewActivations(selectedCompany);
    } catch (err) {
      console.error('Error desbloqueando dispositivo:', err);
      setError('Error al desbloquear el dispositivo');
    }
  };

  const handleRevokeDevice = async (device: Device) => {
    if (!selectedCompany || !window.confirm('¿Estás seguro de que deseas desvincular este dispositivo? Al desvincularlo, la caja ya no podrá sincronizar y liberará un cupo de activación.')) return;
    try {
      await revokeDevice(device.id);
      setSuccessMessage('Dispositivo desvinculado correctamente');
      setTimeout(() => setSuccessMessage(null), 3000);
      handleViewActivations(selectedCompany);
    } catch (err) {
      console.error('Error revocando dispositivo:', err);
      setError('Error al revocar el dispositivo');
    }
  };

  const handleReactivateDevice = async (device: Device) => {
    if (!selectedCompany || !window.confirm('¿Estás seguro de que deseas reactivar este dispositivo? Consumirá un cupo de activación.')) return;
    try {
      await reactivateDevice(device.id);
      setSuccessMessage('Dispositivo reactivado correctamente');
      setTimeout(() => setSuccessMessage(null), 3000);
      handleViewActivations(selectedCompany);
    } catch (err: any) {
      console.error('Error reactivando dispositivo:', err);
      setError(err.response?.data?.message || 'Error al reactivar el dispositivo');
    }
  };

  const handleOpenBilling = (company: Company) => {
    setSelectedCompany(company);
    setFormData({
      ...formData,
      billingEnabled: company.billingEnabled || false,
      razonSocial: company.razonSocial || '',
      cityId: company.cityId || '',
      taxRegimeId: company.taxRegimeId || '',
      factusClientId: company.factusClientId || '',
      factusClientSecret: company.factusClientSecret || '',
      factusUsername: company.factusUsername || '',
      factusPassword: company.factusPassword || '',
    });
    setShowBillingModal(true);
  };

  const handleUpdateBilling = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompany) return;

    setIsSubmitting(true);
    setError(null);

    // Prepare billing specific data only
    const billingData = {
      billingEnabled: formData.billingEnabled,
      razonSocial: formData.razonSocial,
      cityId: formData.cityId,
      taxRegimeId: formData.taxRegimeId,
      factusClientId: formData.factusClientId,
      factusClientSecret: formData.factusClientSecret,
      factusUsername: formData.factusUsername,
      factusPassword: formData.factusPassword
    };

    try {
      await updateCompany(selectedCompany.id, billingData);
      setShowBillingModal(false);
      setSelectedCompany(null);
      resetForm();
      setSuccessMessage('Configuración de facturación actualizada');
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      console.error('Error actualizando facturación:', err);
      setError('Error al actualizar la configuración de facturación');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenCreateUser = (company: Company) => {
    setSelectedCompany(company);
    setUserFormData({
      nombre: '',
      email: '',
      password: '',
      rol: 'ADMIN',
      telefono: '',
      companyId: company.id,
    });
    setShowCreateUserModal(true);
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await createUser(userFormData);
      setShowCreateUserModal(false);
      setSuccessMessage('Usuario creado exitosamente');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      console.error('Error creando usuario:', err);
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Error al crear el usuario');
      } else {
        setError('Error al crear el usuario');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Sanitize payload
    const payload = {
      ...formData,
      planId: formData.planId === '' ? null : formData.planId,
    };

    try {
      await createCompany(payload);
      setShowAddModal(false);
      resetForm();
      setSuccessMessage('Empresa creada exitosamente');
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      console.error('Error creando empresa:', err);
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Error al crear la empresa');
      } else {
        setError('Error al crear la empresa');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompany) return;

    setIsSubmitting(true);
    setError(null);

    // Sanitize payload
    const payload = {
      ...formData,
      planId: formData.planId === '' ? null : formData.planId,
    };

    try {
      await updateCompany(selectedCompany.id, payload);
      setShowEditModal(false);
      setSelectedCompany(null);
      resetForm();
      setSuccessMessage('Empresa actualizada exitosamente');
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      console.error('Error actualizando empresa:', err);
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Error al actualizar la empresa');
      } else {
        setError('Error al actualizar la empresa');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCompany) return;

    const expectedText = `eliminar ${selectedCompany.nombre}`.toLowerCase();
    if (deleteConfirmText.toLowerCase() !== expectedText) {
      setError('El texto de confirmación no coincide');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await deleteCompany(selectedCompany.id);
      setShowDeleteModal(false);
      setSelectedCompany(null);
      setDeleteConfirmText('');
      setSuccessMessage('Empresa eliminada exitosamente');
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      console.error('Error eliminando empresa:', err);
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Error al eliminar la empresa');
      } else {
        setError('Error al eliminar la empresa');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeStatus = async (company: Company, newStatus: 'ACTIVA' | 'INACTIVA' | 'PRUEBA') => {
    try {
      await updateCompany(company.id, { status: newStatus });
      setSuccessMessage(`Estado actualizado a ${getStatusLabel(newStatus)}`);
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error actualizando estado:', err);
    }
  };

  const handleToggleStore = async (company: Company) => {
    const newValue = !company.storeEnabled;
    try {
      await updateCompany(company.id, { storeEnabled: newValue });
      setSuccessMessage(`Módulo de tienda ${newValue ? 'activado' : 'desactivado'} exitosamente`);
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error actualizando módulo de tienda:', err);
      setError('Error al actualizar el módulo de tienda');
    }
  };

  const handleUpdateMaxSedes = async (company: Company, maxSedes: number) => {
    if (!Number.isInteger(maxSedes) || maxSedes < 1 || maxSedes === company.maxSedes) return;
    try {
      await updateCompany(company.id, { maxSedes });
      setSuccessMessage(`Límite de sedes actualizado a ${maxSedes}`);
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error actualizando límite de sedes:', err);
      setError(err?.response?.data?.message || 'Error al actualizar el límite de sedes');
    }
  };

  const handleAssignDeviceSede = async (device: Device, sedeId: string) => {
    try {
      const updated = await assignDeviceSede(device.id, sedeId);
      setCompanyDevices((prev) => prev.map((d) => (d.id === device.id ? { ...d, ...updated } : d)));
      setSuccessMessage(`Equipo asignado a ${updated.sede?.nombre}. Se aplicará en su próxima sincronización.`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al cambiar la sede del equipo');
    }
  };

  const handleToggleExpenses = async (company: Company) => {
    const newValue = !company.expensesEnabled;
    try {
      await updateCompany(company.id, { expensesEnabled: newValue });
      setSuccessMessage(`Módulo de gastos ${newValue ? 'activado' : 'desactivado'} exitosamente`);
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error actualizando módulo de gastos:', err);
      setError('Error al actualizar el módulo de gastos');
    }
  };





  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando empresas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mensajes de feedback */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-[60] bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fade-in">
          <i className="ri-check-line"></i>
          {successMessage}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-500 text-xl"></i>
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <i className="ri-close-line text-red-500"></i>
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Empresas Clientes</h1>
          <p className="text-slate-500 mt-1">Gestiona los parqueaderos que contratan tu servicio SaaS</p>
        </div>
        <button
          onClick={handleOpenAdd}
          className="px-4 py-2 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition flex items-center gap-2 whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          <span className="text-sm font-medium">Nueva Empresa</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">Total Empresas</p>
              <p className="text-3xl font-bold text-slate-800 mt-2">{stats?.total || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center text-white">
              <i className="ri-building-line text-xl"></i>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">Activas</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{stats?.activas || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center text-white">
              <i className="ri-checkbox-circle-line text-xl"></i>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">En Prueba</p>
              <p className="text-3xl font-bold text-yellow-600 mt-2">{stats?.prueba || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-yellow-500 flex items-center justify-center text-white">
              <i className="ri-time-line text-xl"></i>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">Inactivas</p>
              <p className="text-3xl font-bold text-red-600 mt-2">{stats?.inactivas || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-red-500 flex items-center justify-center text-white">
              <i className="ri-close-circle-line text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input
              type="text"
              placeholder="Buscar empresa o NIT..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
            />
          </div>
          <select
            value={filterPlan}
            onChange={(e) => setFilterPlan(e.target.value)}
            className="px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
          >
            <option value="all">Todos los planes</option>
            {plans.map(plan => (
              <option key={plan.id} value={plan.nombre}>{plan.nombre}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
          >
            <option value="all">Todos los estados</option>
            <option value="ACTIVA">Activa</option>
            <option value="PRUEBA">En Prueba</option>
            <option value="INACTIVA">Inactiva</option>
          </select>
        </div>
      </div>

      {/* Companies Grid */}
      {filteredCompanies.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 border border-slate-200 text-center">
          <i className="ri-building-line text-6xl text-slate-300 mb-4"></i>
          <h3 className="text-xl font-bold text-slate-800 mb-2">No hay empresas</h3>
          <p className="text-slate-500 mb-4">
            {searchTerm || filterPlan !== 'all' || filterStatus !== 'all'
              ? 'No se encontraron empresas con los filtros aplicados'
              : 'Aún no has registrado ninguna empresa'}
          </p>
          {!searchTerm && filterPlan === 'all' && filterStatus === 'all' && (
            <button
              onClick={handleOpenAdd}
              className="px-6 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition font-medium"
            >
              Registrar Primera Empresa
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCompanies.map((company) => (
            <div key={company.id} className="bg-white rounded-2xl border border-slate-200 hover:shadow-lg transition overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold text-lg">
                      {company.nombre.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800">{company.nombre}</h3>
                      <p className="text-xs text-slate-500">{company.nit}</p>
                    </div>
                  </div>
                  {/* Menú de acciones */}
                  <div className="relative group">
                    <button className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
                      <i className="ri-more-2-fill text-slate-500"></i>
                    </button>
                    <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl shadow-lg border border-slate-200 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                      <button
                        onClick={() => handleOpenEdit(company)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                      >
                        <i className="ri-edit-line text-blue-500"></i> Editar
                      </button>
                      {company.status !== 'ACTIVA' && (
                        <button
                          onClick={() => handleChangeStatus(company, 'ACTIVA')}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                        >
                          <i className="ri-checkbox-circle-line text-green-500"></i> Activar
                        </button>
                      )}
                      {company.status !== 'PRUEBA' && (
                        <button
                          onClick={() => handleChangeStatus(company, 'PRUEBA')}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                        >
                          <i className="ri-test-tube-line text-amber-500"></i> Modo Prueba
                        </button>
                      )}
                      {company.status !== 'INACTIVA' && (
                        <button
                          onClick={() => handleChangeStatus(company, 'INACTIVA')}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                        >
                          <i className="ri-pause-circle-line text-red-500"></i> Suspender
                        </button>
                      )}

                      <button
                        onClick={() => handleOpenBilling(company)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                      >
                        <i className="ri-secure-payment-line text-indigo-500"></i> Configurar Fac.
                      </button>
                      <button
                        onClick={() => handleOpenCreateUser(company)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                      >
                        <i className="ri-user-add-line text-teal-500"></i> Crear Usuario
                      </button>
                      <button
                        onClick={() => handleViewActivations(company)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                      >
                        <i className="ri-computer-line text-blue-500"></i> Ver Activaciones
                      </button>
                      <button
                        onClick={() => handleOpenDelete(company)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                      >
                        <i className="ri-delete-bin-line"></i> Eliminar
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <span className={`px-3 py-1 rounded-lg text-xs font-medium ${getPlanColor(company.plan?.nombre)}`}>
                    {company.plan?.nombre || 'Sin Plan'}
                  </span>
                  <span className={`px-3 py-1 rounded-lg text-xs font-medium ${getStatusColor(company.status)}`}>
                    {getStatusLabel(company.status)}
                  </span>
                  {company.billingEnabled && (
                    <span className="px-3 py-1 rounded-lg text-xs font-medium bg-indigo-100 text-indigo-700" title="Facturación Habilitada">
                      <i className="ri-file-list-3-line mr-1"></i>
                      Facturación
                    </span>
                  )}
                  {company.expensesEnabled && (
                    <span className="px-3 py-1 rounded-lg text-xs font-medium bg-green-100 text-green-700" title="Módulo de Gastos Habilitado">
                      <i className="ri-money-dollar-circle-line mr-1"></i>
                      Gastos
                    </span>
                  )}
                  {company.storeEnabled && (
                    <span className="px-3 py-1 rounded-lg text-xs font-medium bg-purple-100 text-purple-700" title="Módulo de Tienda Habilitado">
                      <i className="ri-store-2-line mr-1"></i>
                      Tienda
                    </span>
                  )}
                </div>

                {/* Toggle Módulo de Gastos */}
                <div className="mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ri-money-dollar-circle-line text-green-600"></i>
                      <span className="text-sm font-medium text-slate-700">Módulo de Gastos</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={company.expensesEnabled || false}
                        onChange={() => handleToggleExpenses(company)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                </div>

                {/* Toggle Módulo de Tienda */}
                <div className="mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ri-store-2-line text-purple-600"></i>
                      <span className="text-sm font-medium text-slate-700">Módulo de Tienda</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={company.storeEnabled || false}
                        onChange={() => handleToggleStore(company)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                </div>

                {/* Multi sede: límite de sedes */}
                <div className="mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <i className="ri-map-pin-2-line text-teal-600"></i>
                    <span className="text-sm font-medium text-slate-700">Sedes</span>
                    <span className="text-xs text-slate-500">({company.sedes ?? 0} creadas)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Máx.</span>
                    <input
                      key={`${company.id}-${company.maxSedes}`}
                      type="number"
                      min={Math.max(1, company.sedes ?? 1)}
                      defaultValue={company.maxSedes ?? 1}
                      onBlur={(e) => handleUpdateMaxSedes(company, parseInt(e.target.value))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      }}
                      className="w-16 px-2 py-1 border border-slate-200 rounded-lg text-sm text-center"
                      title="Número máximo de sedes que puede crear la empresa"
                    />
                  </div>
                </div>

                {/* Código de Activación */}
                {company.activationCode && (
                  <div className="mb-4 p-3 bg-blue-50/50 rounded-xl border border-blue-100 flex items-center justify-between group/code">
                    <div className="flex flex-col">
                      <span className="text-[10px] text-blue-500 font-bold uppercase tracking-wider">Software Desktop</span>
                      <span className="text-sm font-mono font-bold text-blue-700 tracking-widest">{company.activationCode}</span>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(company.activationCode!);
                        setSuccessMessage('Código copiado al portapapeles');
                        setTimeout(() => setSuccessMessage(null), 3000);
                      }}
                      className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-blue-200 text-blue-500 hover:bg-blue-500 hover:text-white transition shadow-sm"
                      title="Copiar código de activación"
                    >
                      <i className="ri-file-copy-line"></i>
                    </button>
                  </div>
                )}

                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Espacios:</span>
                    <span className="font-semibold text-slate-800">{company.espacios || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Usuarios:</span>
                    <span className="font-semibold text-slate-800">{company.usuarios || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Vehículos:</span>
                    <span className="font-semibold text-slate-800">{company.vehiculos || 0}</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <button
                    onClick={() => handleViewDetails(company)}
                    className="w-full px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition text-sm font-medium whitespace-nowrap"
                  >
                    <i className="ri-eye-line mr-2"></i>
                    Ver Detalles
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Crear Empresa */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-slate-800">Nueva Empresa</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="w-10 h-10 rounded-xl hover:bg-slate-100 flex items-center justify-center transition"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nombre *</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => handleNombreChange(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Nombre de la empresa"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">NIT *</label>
                <input
                  type="text"
                  value={formData.nit}
                  onChange={(e) => handleNitChange(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Máximo 13 dígitos numéricos"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Plan Suscripción</label>
                <select
                  value={formData.planId || ''}
                  onChange={(e) => setFormData({ ...formData, planId: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                >
                  <option value="">Seleccionar plan...</option>
                  {plans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.nombre} - {formatCOP(plan.precio)} / {plan.periodo}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Dirección</label>
                <input
                  type="text"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Dirección de la empresa"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Teléfono</label>
                  <input
                    type="text"
                    value={formData.telefono}
                    onChange={(e) => handleTelefonoChange(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="3100000000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="email@empresa.com"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition font-medium"
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition font-medium disabled:bg-teal-300 flex items-center justify-center gap-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Creando...
                    </>
                  ) : (
                    'Crear Empresa'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar Empresa */}
      {showEditModal && selectedCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-slate-800">Editar Empresa</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="w-10 h-10 rounded-xl hover:bg-slate-100 flex items-center justify-center transition"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nombre *</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => handleNombreChange(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Nombre de la empresa"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">NIT *</label>
                <input
                  type="text"
                  value={formData.nit}
                  onChange={(e) => handleNitChange(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Máximo 13 dígitos numéricos"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Plan Suscripción</label>
                <select
                  value={formData.planId || ''}
                  onChange={(e) => setFormData({ ...formData, planId: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                >
                  <option value="">Seleccionar plan...</option>
                  {plans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.nombre} - {formatCOP(plan.precio)} / {plan.periodo}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Dirección</label>
                <input
                  type="text"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Dirección de la empresa"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Teléfono</label>
                  <input
                    type="text"
                    value={formData.telefono}
                    onChange={(e) => handleTelefonoChange(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="+57 310 000 0000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="email@empresa.com"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition font-medium"
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition font-medium disabled:bg-teal-300 flex items-center justify-center gap-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Guardando...
                    </>
                  ) : (
                    'Guardar Cambios'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Ver Detalles */}
      {showDetailsModal && selectedCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-slate-800">Detalles de la Empresa</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="w-10 h-10 rounded-xl hover:bg-slate-100 flex items-center justify-center transition"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold text-2xl">
                  {selectedCompany.nombre.charAt(0)}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800">{selectedCompany.nombre}</h3>
                  <p className="text-slate-500">{selectedCompany.nit}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500 mb-1">Plan Actual</p>
                  <p className="font-bold text-slate-800">{selectedCompany.plan?.nombre || 'Sin Plan'}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500 mb-1">Estado</p>
                  <span className={`inline-block px-3 py-1 rounded-lg text-xs font-medium ${getStatusColor(selectedCompany.status)}`}>
                    {getStatusLabel(selectedCompany.status)}
                  </span>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500 mb-1">Espacios</p>
                  <p className="font-bold text-slate-800">{selectedCompany.espacios || 0}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500 mb-1">Usuarios</p>
                  <p className="font-bold text-slate-800">{selectedCompany.usuarios || 0}</p>
                </div>
              </div>

              <div className="space-y-3">
                {selectedCompany.direccion && (
                  <div className="flex items-start gap-3">
                    <i className="ri-map-pin-line text-teal-600 text-xl mt-1"></i>
                    <div>
                      <p className="text-sm text-slate-500">Dirección</p>
                      <p className="font-medium text-slate-800">{selectedCompany.direccion}</p>
                    </div>
                  </div>
                )}
                {selectedCompany.telefono && (
                  <div className="flex items-start gap-3">
                    <i className="ri-phone-line text-teal-600 text-xl mt-1"></i>
                    <div>
                      <p className="text-sm text-slate-500">Teléfono</p>
                      <p className="font-medium text-slate-800">{selectedCompany.telefono}</p>
                    </div>
                  </div>
                )}
                {selectedCompany.email && (
                  <div className="flex items-start gap-3">
                    <i className="ri-mail-line text-teal-600 text-xl mt-1"></i>
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium text-slate-800">{selectedCompany.email}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <i className="ri-calendar-line text-teal-600 text-xl mt-1"></i>
                  <div>
                    <p className="text-sm text-slate-500">Fecha de Registro</p>
                    <p className="font-medium text-slate-800">
                      {new Date(selectedCompany.fechaRegistro).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleOpenEdit(selectedCompany);
                  }}
                  className="flex-1 px-4 py-3 border border-teal-500 text-teal-600 rounded-xl hover:bg-teal-50 transition font-medium"
                >
                  <i className="ri-edit-line mr-2"></i>
                  Editar
                </button>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="flex-1 px-4 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition font-medium"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Eliminar Empresa */}
      {showDeleteModal && selectedCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-red-600">Eliminar Empresa</h2>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmText('');
                    setError(null);
                  }}
                  className="w-10 h-10 rounded-xl hover:bg-slate-100 flex items-center justify-center transition"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex gap-3">
                  <i className="ri-alert-line text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <div className="flex-1">
                    <p className="font-semibold text-red-800 mb-2">¡Advertencia! Esta acción es irreversible</p>
                    <p className="text-sm text-red-700 mb-2">
                      Se eliminarán permanentemente todos los datos de <strong>{selectedCompany.nombre}</strong>, incluyendo:
                    </p>
                    <ul className="list-disc list-inside text-sm text-red-700 space-y-1 ml-2">
                      <li>Todos los usuarios ({selectedCompany.usuarios || 0})</li>
                      <li>Todos los vehículos ({selectedCompany.vehiculos || 0})</li>
                      <li>Todos los espacios ({selectedCompany.espacios || 0})</li>
                      <li>Movimientos, contratos, pagos y turnos</li>
                      <li>Facturas electrónicas y logs de auditoría</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Para confirmar, escribe: <br />
                  <code className="text-red-600 bg-red-50 px-2 py-1 rounded mt-1 inline-block">
                    eliminar {selectedCompany.nombre}
                  </code>
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder={`eliminar ${selectedCompany.nombre}`}
                  autoFocus
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500"></i>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmText('');
                    setError(null);
                  }}
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition font-medium"
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition font-medium disabled:bg-red-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  disabled={isSubmitting || deleteConfirmText.toLowerCase() !== `eliminar ${selectedCompany.nombre}`.toLowerCase()}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Eliminando...
                    </>
                  ) : (
                    'Confirmar Eliminación'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Configuración Facturación */}
      {showBillingModal && selectedCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-slate-800">Facturación Electrónica</h2>
                <button
                  onClick={() => setShowBillingModal(false)}
                  className="w-10 h-10 rounded-xl hover:bg-slate-100 flex items-center justify-center transition"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <p className="text-sm text-slate-500 mt-1">Configura la integración con Factus para {selectedCompany.nombre}</p>
            </div>
            <form onSubmit={handleUpdateBilling} className="p-6 space-y-4">

              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl mb-4">
                <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out cursor-pointer">
                  <input
                    type="checkbox"
                    id="billingEnabledModal"
                    className="absolute w-0 h-0 opacity-0"
                    checked={formData.billingEnabled}
                    onChange={(e) => setFormData({ ...formData, billingEnabled: e.target.checked })}
                  />
                  <label
                    htmlFor="billingEnabledModal"
                    className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ${formData.billingEnabled ? 'bg-teal-500' : 'bg-slate-300'}`}
                  ></label>
                  <label
                    htmlFor="billingEnabledModal"
                    className={`absolute left-0 top-0 w-6 h-6 bg-white rounded-full shadow transform transition-transform duration-200 ${formData.billingEnabled ? 'translate-x-6' : 'translate-x-0'}`}
                  ></label>
                </div>
                <label htmlFor="billingEnabledModal" className="text-sm font-medium text-slate-700 cursor-pointer select-none">
                  Habilitar Facturación Electrónica
                </label>
              </div>

              {formData.billingEnabled && (
                <div className="space-y-4 animate-fade-in">
                  <div className="bg-blue-50 p-4 rounded-xl flex items-start gap-3">
                    <i className="ri-information-line text-blue-500 mt-0.5"></i>
                    <p className="text-sm text-blue-700">Ingresa las credenciales proporcionadas por Factus (solo credenciales).</p>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Factus Client ID *</label>
                      <input
                        type="text"
                        value={formData.factusClientId}
                        onChange={(e) => setFormData({ ...formData, factusClientId: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="client_id"
                        required={formData.billingEnabled}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Factus Client Secret *</label>
                      <input
                        type="password"
                        value={formData.factusClientSecret}
                        onChange={(e) => setFormData({ ...formData, factusClientSecret: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="client_secret"
                        required={formData.billingEnabled}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Correo / Usuario *</label>
                        <input
                          type="text"
                          value={formData.factusUsername}
                          onChange={(e) => setFormData({ ...formData, factusUsername: e.target.value })}
                          className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                          placeholder="sandbox@factus.com.co"
                          required={formData.billingEnabled}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Contraseña *</label>
                        <input
                          type="password"
                          value={formData.factusPassword}
                          onChange={(e) => setFormData({ ...formData, factusPassword: e.target.value })}
                          className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                          placeholder="Contraseña de API"
                          required={formData.billingEnabled}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-slate-100 mt-6">
                <button
                  type="button"
                  onClick={() => setShowBillingModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition font-medium"
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-medium disabled:bg-indigo-300 flex items-center justify-center gap-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Guardando...
                    </>
                  ) : (
                    'Guardar Configuración'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Modal Crear Usuario */}
      {showCreateUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-slate-800">Crear Usuario</h2>
                <button
                  onClick={() => setShowCreateUserModal(false)}
                  className="w-10 h-10 rounded-xl hover:bg-slate-100 flex items-center justify-center transition"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <p className="text-sm text-slate-500 mt-1">Crear usuario para {selectedCompany?.nombre}</p>
            </div>
            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nombre Completo *</label>
                <input
                  type="text"
                  value={userFormData.nombre}
                  onChange={(e) => setUserFormData({ ...userFormData, nombre: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Contraseña *</label>
                <input
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                  minLength={6}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Rol *</label>
                  <select
                    value={userFormData.rol}
                    onChange={(e) => setUserFormData({ ...userFormData, rol: e.target.value as 'ADMIN' | 'OPERADOR' })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                  >
                    <option value="ADMIN">Administrador</option>
                    <option value="OPERADOR">Operador</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Teléfono *</label>
                  <input
                    type="text"
                    value={userFormData.telefono}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^[0-9]*$/.test(val) && val.length <= 15) {
                        setUserFormData({ ...userFormData, telefono: val });
                      }
                    }}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-slate-100 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateUserModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition font-medium"
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition font-medium disabled:bg-teal-300 flex items-center justify-center gap-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Creando...
                    </>
                  ) : (
                    'Crear Usuario'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Ver Activaciones / Dispositivos */}
      {showActivationsModal && selectedCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Activaciones Desktop</h2>
                  <p className="text-sm text-slate-500 mt-1">Dispositivos registrados para {selectedCompany.nombre}</p>
                </div>
                <button
                  onClick={() => setShowActivationsModal(false)}
                  className="w-10 h-10 rounded-xl hover:bg-slate-100 flex items-center justify-center transition"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Configuración de Código */}
              {activeActivationCode && (
                <div className="mb-8 p-6 bg-slate-50 rounded-2xl border border-slate-200">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">Configuración del Código</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xl font-mono font-bold text-blue-600">{activeActivationCode.code}</span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${activeActivationCode.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                          activeActivationCode.status === 'EXHAUSTED' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                          {activeActivationCode.status === 'ACTIVE' ? 'Disponible' :
                            activeActivationCode.status === 'EXHAUSTED' ? 'Agotado' :
                              activeActivationCode.status}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-end gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Cupos Máximos</label>
                        <input
                          type="number"
                          min={activeActivationCode.usedDevices}
                          value={newMaxDevices}
                          onChange={(e) => setNewMaxDevices(parseInt(e.target.value))}
                          className="w-24 px-3 py-2 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        />
                      </div>
                      <button
                        onClick={handleUpdateMaxDevices}
                        disabled={isUpdatingLimit || newMaxDevices === activeActivationCode.maxDevices}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                      >
                        {isUpdatingLimit ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
                        Guardar
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-6 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <i className="ri-computer-line text-slate-400"></i>
                      <span>Usados: <strong>{activeActivationCode.usedDevices}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-slate-400"></i>
                      <span>Disponibles: <strong>{Math.max(0, activeActivationCode.maxDevices - activeActivationCode.usedDevices)}</strong></span>
                    </div>
                  </div>
                </div>
              )}

              {/* Plazo sin conexión */}
              <div className="mb-8 p-6 bg-slate-50 rounded-2xl border border-slate-200">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">Operación sin internet</h3>
                    <p className="text-sm text-slate-600 mt-1 max-w-md">
                      Días que cada equipo puede trabajar sin sincronizar antes de bloquearse. El equipo avisa con anticipación
                      y se desbloquea solo al conectarse a internet.
                    </p>
                  </div>
                  <div className="flex items-end gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Días permitidos</label>
                      <input
                        type="number"
                        min={1}
                        max={365}
                        value={Number.isNaN(offlineDays) ? '' : offlineDays}
                        onChange={(e) => setOfflineDays(parseInt(e.target.value))}
                        className="w-24 px-3 py-2 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    </div>
                    <button
                      onClick={handleSaveOfflineDays}
                      disabled={isSavingOfflineDays || offlineDays === (selectedCompany.offlineGraceDays ?? 7)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                    >
                      {isSavingOfflineDays ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
                      Guardar
                    </button>
                  </div>
                </div>
                {offlineDaysError && <p className="mt-3 text-sm text-red-600">{offlineDaysError}</p>}
              </div>

              {isLoadingDevices ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-slate-500">Cargando dispositivos activados...</p>
                </div>
              ) : companyDevices.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-300">
                  <i className="ri-computer-line text-5xl text-slate-300 mb-4"></i>
                  <h3 className="text-lg font-bold text-slate-700">Sin activaciones</h3>
                  <p className="text-slate-500 max-w-xs mx-auto">Esta empresa aún no ha activado el software en ningún dispositivo desktop.</p>
                  <div className="mt-6 p-4 bg-blue-50 rounded-xl inline-block">
                    <p className="text-sm font-medium text-blue-700">Código de Activación: <span className="font-mono font-bold ml-1">{selectedCompany.activationCode}</span></p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {companyDevices.map((device) => (
                      <div key={device.id} className={`p-5 rounded-2xl border transition-all ${device.status === 'BLOCKED' ? 'bg-red-50 border-red-100' : 'bg-white border-slate-200 shadow-sm hover:shadow-md'}`}>
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white ${device.status === 'BLOCKED' ? 'bg-red-500' : 'bg-blue-600'}`}>
                              <i className="ri-computer-line text-xl"></i>
                            </div>
                            <div>
                              <h4 className="font-bold text-slate-800">{device.name || 'Dispositivo Desconocido'}</h4>
                              <p className="text-xs text-slate-500 font-mono truncate max-w-[150px]">{device.deviceId}</p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${device.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                            device.status === 'BLOCKED' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                            {device.status === 'ACTIVE' ? 'Activo' : device.status === 'BLOCKED' ? 'Bloqueado' : 'Desvinculado'}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-xs">
                          <div>
                            <p className="text-slate-400 mb-0.5">Plataforma</p>
                            <p className="font-medium text-slate-700 capitalize">{device.platform || 'Desconocida'}</p>
                          </div>
                          <div>
                            <p className="text-slate-400 mb-0.5">Hostname</p>
                            <p className="font-medium text-slate-700 truncate">{device.hostname || 'N/A'}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-slate-400 mb-0.5">Sede</p>
                            {companySedes.length > 1 && device.status !== 'REVOKED' ? (
                              <select
                                value={device.sedeId ?? ''}
                                onChange={(e) => e.target.value && handleAssignDeviceSede(device, e.target.value)}
                                className="w-full px-2 py-1 border border-slate-200 rounded-lg text-xs bg-white"
                              >
                                {!device.sedeId && <option value="">Sin sede</option>}
                                {companySedes.map((sede) => (
                                  <option key={sede.id} value={sede.id}>{sede.nombre}</option>
                                ))}
                              </select>
                            ) : (
                              <p className="font-medium text-slate-700">{device.sede?.nombre || 'Sin sede'}</p>
                            )}
                          </div>
                          <div>
                            <p className="text-slate-400 mb-0.5">Última Conexión</p>
                            <p className="font-medium text-slate-700">{device.lastSeen ? new Date(device.lastSeen).toLocaleString() : 'Nunca'}</p>
                          </div>
                          <div>
                            <p className="text-slate-400 mb-0.5">Último Sync</p>
                            <p className="font-medium text-slate-700">{device.lastSyncAt ? new Date(device.lastSyncAt).toLocaleString() : 'Nunca'}</p>
                          </div>
                          {device.status === 'ACTIVE' && (() => {
                            const deadline = getOfflineDeadline(device);
                            if (!deadline) return null;
                            const msLeft = deadline.getTime() - Date.now();
                            const expired = msLeft <= 0;
                            const soon = !expired && msLeft < 24 * 60 * 60 * 1000;
                            return (
                              <div className="col-span-2">
                                <p className="text-slate-400 mb-0.5">Bloqueo sin conexión</p>
                                <p className={`font-medium ${expired ? 'text-red-600' : soon ? 'text-amber-600' : 'text-slate-700'}`}>
                                  {expired ? `Bloqueado por falta de sincronización desde ${deadline.toLocaleString()}` : `Si no sincroniza, se bloquea el ${deadline.toLocaleString()}`}
                                </p>
                              </div>
                            );
                          })()}
                        </div>

                        <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
                          {device.status === 'ACTIVE' ? (
                            <button
                              onClick={() => handleBlockDevice(device)}
                              className="flex-1 px-3 py-2 bg-amber-500 text-white rounded-lg text-xs font-bold hover:bg-amber-600 transition flex items-center justify-center gap-1"
                            >
                              <i className="ri-lock-2-line"></i> BLOQUEAR
                            </button>
                          ) : device.status === 'BLOCKED' ? (
                            <button
                              onClick={() => handleUnblockDevice(device)}
                              className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-xs font-bold hover:bg-green-700 transition flex items-center justify-center gap-1"
                            >
                              <i className="ri-lock-unlock-line"></i> DESBLOQUEAR
                            </button>
                          ) : null}

                          <button
                            onClick={() => handleRevokeDevice(device)}
                            className="flex-1 px-3 py-2 border border-red-200 text-red-600 rounded-lg text-xs font-bold hover:bg-red-50 transition flex items-center justify-center gap-1"
                          >
                            <i className="ri-delete-bin-line"></i> DESVINCULAR
                          </button>
                          {device.status === 'REVOKED' && (
                            <button
                              onClick={() => handleReactivateDevice(device)}
                              className="flex-1 px-3 py-2 border border-blue-200 text-blue-600 rounded-lg text-xs font-bold hover:bg-blue-50 transition flex items-center justify-center gap-1"
                            >
                              <i className="ri-refresh-line"></i> REACTIVAR
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <button
                onClick={() => setShowActivationsModal(false)}
                className="w-full px-4 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 transition font-bold shadow-sm"
              >
                Cerrar Panel de Activaciones
              </button>
            </div>
          </div>
        </div>
      )}


    </div>

  );
}
